/**
 * Wheel: a dial with any number of places, and a hop that goes round it.
 *
 * This is modular arithmetic, and nothing in it says so. A child sets a wheel
 * of 12, hops by 5, and finds they eventually stand on every place. They set
 * the hop to 3 and find they only ever touch four of them and come home fast.
 * The reason is the highest common factor. They will meet that name years
 * later; here they just notice that some hops go everywhere and some do not,
 * which is the part that is hard to be told.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, Play, RotateCcw, Square } from 'lucide-preact';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play, playNote } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { cycleLength, landings, positionAfter, reachable, visitsEverywhere, wheelLine } from '../../lib/wheel.ts';
import { Chip, Coach, Stepper, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

const TOP = 150;
const BOTTOM = 76;
const STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export function WheelToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const clockRef = useRef(0);
  /** Eased hop position, so the hand swings round rather than teleporting. */
  const handRef = useRef(0);

  const [size, setSize] = useState(12);
  const [step, setStep] = useState(5);
  const [hops, setHops] = useState(0);
  const [auto, setAuto] = useState(false);

  const at = positionAfter(hops, step, size);
  const home = cycleLength(step, size);
  const everywhere = visitsEverywhere(step, size);

  const live = useRef({ size, step, hops });
  live.current = { size, step, hops };

  const hop = () => {
    const next = live.current.hops + 1;
    setHops(next);
    // Each place on the wheel is a note, so going round is a phrase that
    // repeats exactly when the hops do.
    playNote(positionAfter(next, live.current.step, live.current.size), { duration: 0.28, gain: 0.2 });
  };

  useEffect(() => {
    if (!auto) return;
    const timer = setInterval(hop, 480);
    return () => clearInterval(timer);
  }, [auto]);

  // Coming home is the moment worth noticing, so it is the moment that pops.
  const wasHome = useRef(false);
  useEffect(() => {
    const back = hops > 0 && at === 0;
    if (back && !wasHome.current) {
      play('yay', 0.7);
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) spawnSparks(sparksRef.current, { x: rect.width / 2, y: rect.height * 0.5 }, partyColors(), 22, 380);
    }
    wasHome.current = back;
  }, [hops, at]);

  const restart = (nextSize = size, nextStep = step) => {
    setSize(nextSize);
    setStep(nextStep);
    setHops(0);
    setAuto(false);
    handRef.current = 0;
    play('whoosh', 0.6);
  };

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      clockRef.current += dt;
      updateSparks(sparksRef.current, dt);
      const { size: places, step: k, hops: count } = live.current;

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      const usableH = height - TOP - BOTTOM;
      const cx = width / 2;
      const cy = TOP + usableH / 2;
      const radius = Math.min(width * 0.38, usableH * 0.44);

      const pointAt = (place: number, r = radius) => {
        const a = (place / places) * Math.PI * 2 - Math.PI / 2;
        return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
      };

      // The rim.
      ctx.save();
      ctx.strokeStyle = token('--paper-line');
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      // The trail of hops, drawn as chords. This is the star, and it is the
      // whole picture of what the hop does to the wheel.
      const visited = landings(count, k, places);
      ctx.save();
      ctx.strokeStyle = token('--crayon-purple');
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.beginPath();
      for (let i = 1; i < visited.length; i++) {
        const from = pointAt(visited[i - 1]);
        const to = pointAt(visited[i]);
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
      }
      ctx.stroke();
      ctx.restore();

      // Every place, lit if it has been stood on.
      const stood = new Set(visited);
      const dotR = Math.max(6, Math.min(16, radius / places + 5));
      for (let place = 0; place < places; place++) {
        const p = pointAt(place);
        const on = stood.has(place);
        ctx.save();
        ctx.fillStyle = on ? token('--crayon-teal') : token('--paper-deep');
        ctx.strokeStyle = on ? 'rgba(44, 37, 69, 0.8)' : token('--ink-faint');
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(p.x, p.y, dotR, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        if (dotR > 9) {
          ctx.fillStyle = on ? '#ffffff' : token('--ink-soft');
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = `700 ${dotR * 0.95}px ui-rounded, system-ui, sans-serif`;
          ctx.fillText(String(place), p.x, p.y);
        }
        ctx.restore();
      }

      // The hand, easing round the short way to where it now points.
      const target = positionAfter(count, k, places);
      const current = handRef.current;
      let delta = target - current;
      while (delta > places / 2) delta -= places;
      while (delta < -places / 2) delta += places;
      handRef.current = current + delta * (1 - Math.exp(-dt * 9));

      const tip = pointAt(handRef.current, radius * 0.82);
      ctx.save();
      ctx.strokeStyle = token('--crayon-red');
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(tip.x, tip.y);
      ctx.stroke();
      ctx.fillStyle = token('--crayon-red');
      ctx.beginPath();
      ctx.arc(cx, cy, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      drawSparks(ctx, sparksRef.current);
    },

    onDown: () => hop(),
  });

  const places = reachable(step, size).length;

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Number wheel" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton round label={auto ? 'Stop hopping' : 'Hop by itself'} hideLabel onClick={() => setAuto((v) => !v)}>
              {auto ? <Square size={22} strokeWidth={2.5} /> : <Play size={24} strokeWidth={2.5} />}
            </ToyButton>
            <ToyButton round label="Back to the start" hideLabel onClick={() => restart()}>
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          <strong>{at}</strong>
          <span>{wheelLine(hops, step, size)}</span>
        </p>

        <Coach>
          {hops === 0
            ? `tap to hop ${step} at a time`
            : everywhere
              ? `hopping ${step} stands on every place, home in ${home}`
              : `hopping ${step} only ever reaches ${places} of the ${size}`}
        </Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row dock__row--scroll" role="group" aria-label="How big a hop">
          {STEPS.filter((n) => n < size || n === 1).map((n) => (
            <Chip key={n} label={String(n)} pressed={step === n} onClick={() => restart(size, n)} />
          ))}
        </div>
        <div class="dock__row">
          <Stepper value={size} min={2} max={24} label="places on the wheel" onChange={(n) => restart(n, Math.min(step, n - 1) || 1)} />
        </div>
      </div>
    </div>
  );
}
